import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      const { access_token } = response.data;
      
      // Store token
      localStorage.setItem('admin_token', access_token);
      
      toast.success('Login successful!');
      navigate('/admin/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      if (error.response?.status === 401) {
        toast.error('Invalid credentials');
      } else {
        toast.error('Login failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-black flex items-center justify-center px-6">
      <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black"></div>
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-20 w-96 h-96 bg-red-600 rounded-full filter blur-[150px]"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-gray-700 rounded-full filter blur-[150px]"></div>
      </div>

      <div className="relative z-10 w-full max-w-md">
        <div className="glass-card p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="mb-6 flex justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_mobile-detailing-11/artifacts/n9c26bxp_Untitled%20design.png" 
                alt="ONYXA Car Polishing & Detailing" 
                className="h-32 w-auto"
              />
            </div>
            <h1 
              data-testid="admin-login-title"
              className="text-3xl font-bold mb-2"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Admin Login
            </h1>
            <p className="text-gray-400 text-sm">ONYXA Dashboard</p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleLogin} data-testid="admin-login-form" className="space-y-6">
            <div data-testid="admin-email-container" className="space-y-2">
              <Label htmlFor="email" className="text-gray-200">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                data-testid="admin-email-input"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="bg-gray-900 border-gray-700 text-white"
                placeholder="onyxadetailing@gmail.com"
              />
            </div>

            <div data-testid="admin-password-container" className="space-y-2">
              <Label htmlFor="password" className="text-gray-200">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                data-testid="admin-password-input"
                value={formData.password}
                onChange={handleInputChange}
                required
                className="bg-gray-900 border-gray-700 text-white"
                placeholder="Enter your password"
              />
            </div>

            <Button
              type="submit"
              data-testid="admin-login-btn"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-red-600 to-red-800 hover:from-red-700 hover:to-red-900 text-white py-6"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Logging in...
                </>
              ) : (
                'Login'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              data-testid="back-to-home-btn"
              onClick={() => navigate('/')}
              className="text-sm text-gray-400 hover:text-red-500 transition-colors"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
